import { useEffect, useRef, useState, useCallback } from "react";
import { useGoogleMaps } from "./useGoogleMaps";

declare global {
  interface Window {
    google: typeof google;
  }
}

interface AddressSuggestion {
  description: string;
  place_id: string;
}

interface UseAddressLookupReturn {
  isScriptLoaded: boolean;
  scriptError: string | null;
  suggestions: AddressSuggestion[];
  isFetching: boolean;
  clearSuggestions: () => void;
}

export const useAddressLookup = (
  inputRef: React.RefObject<HTMLInputElement | null>
): UseAddressLookupReturn => {
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );
  const { isScriptLoaded, scriptError } = useGoogleMaps();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const initializeServices = useCallback(() => {
    try {
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();

      const dummyDiv = document.createElement("div");
      placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
    } catch (error) {
      console.error("Error initializing Google services:", error);
    }
  }, []);

  useEffect(() => {
    if (isScriptLoaded && !scriptError) {
      initializeServices();
    }
  }, [isScriptLoaded, scriptError, initializeServices]);

  const fetchSuggestions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || !input.trim()) {
      setSuggestions([]);
      return;
    }

    setIsFetching(true);

    try {
      const request = {
        input: input,
        types: ["address"],
      };

      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (predictions, status) => {
          setIsFetching(false);

          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(
              predictions.map((prediction) => ({
                description: prediction.description,
                place_id: prediction.place_id,
              }))
            );
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      setIsFetching(false);
      console.error("Error fetching address suggestions:", error);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  useEffect(() => {
    const inputElement = inputRef?.current;
    if (!inputElement || !isScriptLoaded) return;

    let debounceTimer: NodeJS.Timeout;

    const handleInput = (event: Event) => {
      const inputValue = (event.target as HTMLInputElement).value;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        void fetchSuggestions(inputValue);
      }, 300);
    };

    const handleFocus = () => {
      const inputValue = inputElement.value;
      if (inputValue) {
        fetchSuggestions(inputValue);
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        clearSuggestions();
      }, 200);
    };

    inputElement.addEventListener("input", handleInput);
    inputElement.addEventListener("focus", handleFocus);
    inputElement.addEventListener("blur", handleBlur);

    return () => {
      clearTimeout(debounceTimer);
      inputElement.removeEventListener("input", handleInput);
      inputElement.removeEventListener("focus", handleFocus);
      inputElement.removeEventListener("blur", handleBlur);
    };
  }, [isScriptLoaded, inputRef, fetchSuggestions, clearSuggestions]);

  return {
    isScriptLoaded,
    scriptError,
    suggestions,
    isFetching,
    clearSuggestions,
  };
};
